import type { StandardPlacementDimensions } from "./printStandardPlacement";

export type ArtworkSize = { width: number; height: number };

/**
 * MN-8890 wiring — fit artwork to a standard placement envelope (Joe B1b, comment 246918).
 *
 * Rules:
 *  - Preserve aspect ratio — never stretch-to-fill.
 *  - Treat the standard as a CAP, not a target: artwork larger than the standard shrinks uniformly
 *    to fit inside the envelope; artwork already at or below the standard is left ALONE (never
 *    up-scaled — that would blow a low-res upload into a blurry print). The existing low-res guard
 *    continues to apply upstream.
 *
 * `current` and `standard` are both in inches. Returns the new size in inches (rounded to 2dp).
 * Returns `current` unchanged when no resize is needed.
 */
export function fitArtworkToStandard(
  current: ArtworkSize,
  standard: StandardPlacementDimensions
): ArtworkSize {
  const { width, height } = current;
  const { widthInches, heightInches } = standard;

  if (width <= 0 || height <= 0 || widthInches <= 0 || heightInches <= 0) {
    return current;
  }

  const scale = Math.min(widthInches / width, heightInches / height);

  // Cap, not target: only shrink. scale >= 1 means it already fits — leave it alone.
  if (scale >= 1) return current;

  return {
    width: Number((width * scale).toFixed(2)),
    height: Number((height * scale).toFixed(2))
  };
}
